import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// TaEligibilityBadge – badge showing tuition assistance eligibility status
// ---------------------------------------------------------------------------

interface TaEligibilityBadgeSignature {
  Args: {
    /** Whether the program is TA-eligible (default: true) */
    eligible?: boolean;
    /** Covered amount text (e.g. "$5,000/yr") */
    amount?: string;
    /** Whether coverage is partial */
    partial?: boolean;
    /** Partial coverage amount text */
    partialAmount?: string;
    /** Total amount text */
    totalAmount?: string;
  };
  Element: HTMLDivElement;
}

/**
 * Three semantic states, three intent modifiers:
 *   eligible (full)    → success (TA Eligible)
 *   eligible (partial) → warning (Partial Coverage)
 *   not eligible       → neutral (Not TA Eligible)
 *
 * Pill chrome (layout/spacing/radius/border) lives on the base
 * `.tio-eligibility-pill` recipe in `addons/tio-reimagined/src/styles.css`;
 * intent modifier swaps bg/border/color through mode-aware status tokens
 * so dark mode cascades without per-class overrides.
 */
export default class TaEligibilityBadge extends Component<TaEligibilityBadgeSignature> {
  get isEligible() {
    return this.args.eligible !== false;
  }

  get isPartial() {
    return this.args.partial === true;
  }

  get intentClass() {
    if (!this.isEligible) return 'tio-eligibility-pill--neutral';
    return this.isPartial ? 'tio-eligibility-pill--warning' : 'tio-eligibility-pill--success';
  }

  <template>
    <div class="tio-eligibility-pill {{this.intentClass}}" ...attributes>
      {{#if this.isEligible}}
        {{#if this.isPartial}}
          {{! Partial coverage — bolt icon }}
          <svg
            class="w-[18px] h-[18px]"
            fill="currentColor"
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M215.79,118.17a8,8,0,0,0-5-5.66L153.18,90.9l14.66-73.33a8,8,0,0,0-13.69-7l-112,120a8,8,0,0,0,3,13l57.63,21.61L88.16,238.43a8,8,0,0,0,13.69,7l112-120A8,8,0,0,0,215.79,118.17Z"
            ></path>
          </svg>
          <span class="font-medium">Partial Coverage</span>
          {{#if @partialAmount}}
            <span class="tio-eligibility-pill__amount">
              {{@partialAmount}}
              {{#if @totalAmount}}
                of
                {{@totalAmount}}
              {{/if}}
            </span>
          {{/if}}
        {{else}}
          {{! Fully covered — check-in-circle icon }}
          <svg
            class="w-[18px] h-[18px]"
            fill="currentColor"
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"
            ></path>
          </svg>
          <span class="font-medium">TA Eligible</span>
          {{#if @amount}}
            <span class="tio-eligibility-pill__amount">{{@amount}} covered</span>
          {{/if}}
        {{/if}}
      {{else}}
        {{! Not eligible — x-in-circle icon }}
        <svg
          class="w-[18px] h-[18px]"
          fill="currentColor"
          viewBox="0 0 256 256"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M165.66,101.66,139.31,128l26.35,26.34a8,8,0,0,1-11.32,11.32L128,139.31l-26.34,26.35a8,8,0,0,1-11.32-11.32L116.69,128,90.34,101.66a8,8,0,0,1,11.32-11.32L128,116.69l26.34-26.35a8,8,0,0,1,11.32,11.32ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"
          ></path>
        </svg>
        <span>Not TA Eligible</span>
      {{/if}}
    </div>
  </template>
}
